import { checkValidEmail } from '../../../../utils';

const PARTNER_ONLY = ['branch', 'link', 'api_key', 'domain'];

export const CUSTOMER_FIELDS = {
    name: { label: 'Company Name', required: true },
    sector: { label: 'Sector', required: false },
    email: {
        label: 'Email',
        required: true,
        validate: value => checkValidEmail(String(value).trim()) || 'Enter a valid email address.',
    },
    address: { label: 'Physical Address', required: true },
    country: { label: 'Country of Registration', required: true },
    province: { label: 'Province', required: false },
    partner: { label: 'Account Type', required: true },
    contact_no: {
        label: 'Phone',
        required: true,
        validate: value => String(value).replace(/\D/g, '').length >= 9 || 'Enter a valid phone number.',
    },
    logo: { label: 'Company logo', required: true },
    branch: { label: 'Branch on LMS', required: true, partnerOnly: true },
    link: { label: 'Link', required: true, partnerOnly: true },
    api_key: { label: 'API Key', required: true, partnerOnly: true },
    domain: { label: 'Domain', required: true, partnerOnly: true },
};

export const isPartnerAccount = partner => !!partner && partner !== 'enterprise_customer';

export const fieldLabel = key => {
    const rule = CUSTOMER_FIELDS[key];
    if (!rule) return key;
    return `${rule.label}${rule.required ? ' *' : ''}`;
};

const isEmpty = value => {
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === null || String(value).trim() === '';
};

/**
 * Returns `{ field: message }` for every field that blocks saving.
 * `fields` limits the check to the inputs a form actually renders.
 */
export const validateCustomer = (form, { fields = Object.keys(CUSTOMER_FIELDS) } = {}) => {
    const partner = isPartnerAccount(form.partner);
    const errors = {};
    fields.forEach((key) => {
        const rule = CUSTOMER_FIELDS[key];
        if (!rule || (rule.partnerOnly && !partner)) return;
        const value = form[key];
        if (rule.required && isEmpty(value)) {
            errors[key] = `${rule.label} is required.`;
            return;
        }
        if (!isEmpty(value) && rule.validate) {
            const result = rule.validate(value);
            if (result !== true) errors[key] = result;
        }
    });
    return errors;
};

export const summariseErrors = (errors) => {
    const messages = Object.values(errors);
    if (!messages.length) return '';
    return messages.length === 1 ? messages[0] : `${messages.length} fields need attention. They are highlighted in red.`;
};

/** Field errors from an API failure: `{ errors: { field: msg } }` or DRF-style `{ field: [msg] }`. */
export const apiFieldErrors = (result) => {
    const source = (result && typeof result.errors === 'object' && result.errors)
        || (result && typeof result.data === 'object' && !Array.isArray(result.data) && result.data)
        || {};
    const errors = {};
    Object.entries(source).forEach(([key, value]) => {
        if (!CUSTOMER_FIELDS[key] && !PARTNER_ONLY.includes(key)) return;
        const message = Array.isArray(value) ? value.filter(Boolean).join(' ') : value;
        if (typeof message === 'string' && message) errors[key] = message;
    });
    return errors;
};

export const scrollToFirstError = (root) => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
        (root || document).querySelector('.Mui-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
};
